import { useState } from 'react';
import type { AdminActivityEntry } from '@/api/admin';
import { methodColor, statusColor, moduleColor, levelColor } from './logFormat';

interface RowProps {
  entry: AdminActivityEntry;
  time: string;
}

/** HTTP request row: method + path + status + duration. */
function RequestRow({ entry, time }: RowProps) {
  return (
    <div className="flex items-center gap-3 px-4 py-2 text-sm">
      <span className="text-td-light shrink-0 w-16 font-mono text-xs">{time}</span>
      <span className={`shrink-0 px-1.5 py-0.5 rounded text-[11px] font-bold ${methodColor(entry.method)}`}>
        {entry.method}
      </span>
      <span className="text-td-navy font-mono text-xs truncate flex-1">
        {entry.path}{entry.query && <span className="text-td-muted">?{entry.query}</span>}
      </span>
      <span className={`shrink-0 px-1.5 py-0.5 rounded text-[11px] font-medium ${statusColor(entry.status)}`}>
        {entry.status}
      </span>
      <span className="text-td-light text-xs shrink-0 w-14 text-right font-mono">{entry.duration}ms</span>
      <span className="text-xs text-td-muted shrink-0 w-24 text-right truncate">{entry.userName}</span>
    </div>
  );
}

/** Business-action row: module badge + action + detail. */
function ActionRow({ entry, time }: RowProps) {
  return (
    <div className="flex items-center gap-3 px-4 py-2 text-sm bg-td-blue/5 border-l-2 border-td-blue">
      <span className="text-td-light shrink-0 w-16 font-mono text-xs">{time}</span>
      <span className={`shrink-0 px-1.5 py-0.5 rounded text-[11px] font-bold ${moduleColor(entry.module)}`}>
        {entry.module}
      </span>
      <div className="flex-1 min-w-0">
        <span className="font-semibold text-td-navy">{entry.action}</span>
        {entry.detail && <span className="text-td-muted ml-2">{entry.detail}</span>}
      </div>
      <span className="text-xs text-td-muted shrink-0 w-24 text-right truncate">{entry.userName}</span>
    </div>
  );
}

/** Error row: red, with an expandable stack trace. */
function ErrorRow({ entry, time }: RowProps) {
  const [expanded, setExpanded] = useState(false);
  const hasStack = Boolean(entry.stack);

  return (
    <div className="bg-red-50 border-l-2 border-red-500">
      <button
        type="button"
        onClick={() => hasStack && setExpanded(e => !e)}
        className={`w-full flex items-center gap-3 px-4 py-2 text-sm text-left ${hasStack ? 'cursor-pointer hover:bg-red-100' : 'cursor-default'}`}
        aria-expanded={hasStack ? expanded : undefined}
      >
        <span className="text-td-light shrink-0 w-16 font-mono text-xs">{time}</span>
        <span className={`shrink-0 px-1.5 py-0.5 rounded text-[11px] font-bold uppercase ${levelColor(entry.level)}`}>
          {entry.level ?? 'error'}
        </span>
        <span className="text-red-800 text-xs truncate flex-1">{entry.msg}</span>
        {entry.path && <span className="text-td-muted font-mono text-xs truncate max-w-[12rem] hidden sm:inline">{entry.path}</span>}
        {typeof entry.status === 'number' && (
          <span className={`shrink-0 px-1.5 py-0.5 rounded text-[11px] font-medium ${statusColor(entry.status)}`}>
            {entry.status}
          </span>
        )}
        {hasStack && <span className="text-td-light text-xs shrink-0">{expanded ? '▾' : '▸'}</span>}
      </button>
      {expanded && hasStack && (
        <pre className="px-4 pb-3 pt-0 text-[11px] font-mono text-red-900 whitespace-pre-wrap break-all overflow-x-auto">
          {entry.stack}
        </pre>
      )}
    </div>
  );
}

/** Render one merged log entry as the row appropriate to its type. */
export function LogEntryRow({ entry, time }: RowProps) {
  if (entry.type === 'error') return <ErrorRow entry={entry} time={time} />;
  if (entry.type === 'request') return <RequestRow entry={entry} time={time} />;
  return <ActionRow entry={entry} time={time} />;
}
